import { canonicalize } from '@meteor/canonicalize';
import { createLoader, useMutation } from '@meteor/data-hooks';
import { useRef, useState } from 'react';
import { withManagementSDK } from '../../management';
import { withAuth } from '../../user';
import { threadClient } from '../client';

export let threadsLoader = createLoader({
  name: 'threads',
  hash: i => canonicalize(i),
  fetch: (i: {
    agentId?: string;
    groupId?: string;
    instanceId: string;
    cursor?: string;
    withSandbox?: boolean;
    createdAt?: { gt?: Date; lt?: Date };
  }) =>
    withManagementSDK(i, sdk =>
      sdk.instances.threads.list(i.instanceId, {
        ...i,
        order: 'desc',
        limit: 50
      })
    ),
  mutators: {}
});

let createSandboxMutator = (i: { instanceId: string; agentId: string; flowId?: string }) =>
  withAuth(() => threadClient.thread.createSandbox(i));

export let useCreateSandboxThread = () => useMutation(createSandboxMutator);

export let useThreads = (
  instanceId: string | null | undefined,
  {
    groupId,
    agentId,
    withSandbox,
    createdAt
  }: {
    agentId?: string | null | undefined;
    groupId?: string | null | undefined;
    withSandbox?: boolean;
    createdAt?: { gte?: Date; lte?: Date };
  }
) => {
  let [cursor, setCursor] = useState<string>();
  let threads = threadsLoader.use(
    instanceId
      ? {
          agentId: agentId ?? undefined,
          groupId: groupId ?? undefined,
          instanceId,
          cursor,
          createdAt: createdAt ? { gt: createdAt.gte, lt: createdAt.lte } : undefined,
          withSandbox
        }
      : null
  );

  let dataRef = useRef(threads.data);
  dataRef.current = threads.data;

  return {
    ...threads,
    next: () => {
      let c = dataRef.current?.pagination.afterCursor;
      if (c) setCursor(c);
    },
    previous: () => {
      let c = dataRef.current?.pagination.beforeCursor;
      if (c) setCursor(c);
    }
  };
};

export let threadLoader = createLoader({
  name: 'thread',
  hash: i => i.threadId + i.organizationId,
  parents: [threadsLoader],
  fetch: (i: { threadId: string; organizationId: string }) =>
    withAuth(() => threadClient.thread.get(i)),
  mutators: {
    update: (i: { metadata: Record<string, any> }, { input }) =>
      withAuth(() =>
        threadClient.thread.update({
          ...input,
          ...i
        })
      ),

    delete: (_, { input }) => withAuth(() => threadClient.thread.delete(input)),

    createMessage: (
      i: {
        position: { type: 'new' } | { type: 'edit'; messageId: string };
        content: string | null;
        rawInputs?: Record<string, any>;
      },
      { input }
    ) =>
      withAuth(() =>
        threadClient.utterance.create({
          ...input,
          ...i
        })
      )
  }
});

export let useThread = (
  organizationId: string | null | undefined,
  threadId: string | null | undefined
) => {
  let thread = threadLoader.use(
    threadId && organizationId ? { threadId, organizationId } : null
  );

  return {
    ...thread,
    updateMutator: thread.useMutator('update'),
    deleteMutator: thread.useMutator('delete'),
    createMessageMutator: thread.useMutator('createMessage')
  };
};

export let threadEventsLoader = createLoader({
  name: 'threadEvents',
  hash: i => i.threadId + i.organizationId,
  parents: [threadsLoader],
  fetch: (i: { threadId: string; organizationId: string }) =>
    withAuth(() => threadClient.thread.getEvents(i)),
  mutators: {}
});

export let useThreadEvents = (
  organizationId: string | null | undefined,
  threadId: string | null | undefined
) => threadEventsLoader.use(threadId && organizationId ? { threadId, organizationId } : null);

export let threadInputsLoader = createLoader({
  name: 'threadInputs',
  hash: i => i.threadId + i.organizationId,
  parents: [threadsLoader],
  fetch: (i: { threadId: string; organizationId: string }) =>
    withAuth(() => threadClient.thread.getInputs(i)),
  mutators: {}
});

export let useThreadInputs = (
  organizationId: string | null | undefined,
  threadId: string | null | undefined
) => threadInputsLoader.use(threadId && organizationId ? { threadId, organizationId } : null);

export let threadParticipantsLoader = createLoader({
  name: 'threadParticipants',
  hash: i => i.threadId + i.organizationId,
  parents: [threadsLoader],
  fetch: (i: { threadId: string; organizationId: string }) =>
    withAuth(() => threadClient.thread.getParticipants(i)),
  mutators: {}
});

export let useThreadParticipants = (
  organizationId: string | null | undefined,
  threadId: string | null | undefined
) =>
  threadParticipantsLoader.use(
    threadId && organizationId ? { threadId, organizationId } : null
  );
